import { useState } from 'react';
import { useQuery, useMutation } from '@tanstack/react-query';
import { procedureApi } from '../services/procedureApi';
import type { CreateProcedureRequest } from '../types/procedure';

interface NewProcedureModalProps {
  onClose: () => void;
  onSaved: () => void;
}

export default function NewProcedureModal({ onClose, onSaved }: Readonly<NewProcedureModalProps>) {
  const [formData, setFormData] = useState<CreateProcedureRequest>({
    name: '',
    displayName: '',
    serviceGroupId: 0,
    displayOrder: 0,
  });

  const { data: groups } = useQuery({
    queryKey: ['serviceGroups'],
    queryFn: () => procedureApi.getServiceGroups(),
  });

  const mutation = useMutation({
    mutationFn: (data: CreateProcedureRequest) => procedureApi.createProcedure(data),
    onSuccess: () => onSaved(),
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.name.trim()) return;
    if (formData.serviceGroupId === 0) {
      alert('Please select a service group');
      return;
    }
    mutation.mutate({
      ...formData,
      name: formData.name.trim(),
      displayName: formData.displayName?.trim() || null,
    });
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === 'number' ? Number(value) : value,
    }));
  };

  return (
    <div className="modal-overlay">
      <div className="modal">
        <h2>New Procedure</h2>
        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label htmlFor="name">Procedure Name *</label>
            <input
              type="text"
              id="name"
              name="name"
              value={formData.name}
              onChange={handleChange}
              placeholder="Enter procedure name"
              required
              maxLength={200}
              autoFocus
            />
          </div>
          <div className="form-group">
            <label htmlFor="displayName">Display Name (optional)</label>
            <input
              type="text"
              id="displayName"
              name="displayName"
              value={formData.displayName || ''}
              onChange={handleChange}
              placeholder="Override display name"
              maxLength={200}
            />
          </div>
          <div className="form-group">
            <label htmlFor="serviceGroupId">Service Group *</label>
            <select
              id="serviceGroupId"
              name="serviceGroupId"
              value={formData.serviceGroupId}
              onChange={handleChange}
              required
            >
              <option value={0}>Select a group...</option>
              {groups?.map((group) => (
                <option key={group.id} value={group.id}>
                  {group.name}
                </option>
              ))}
            </select>
          </div>
          <div className="form-group">
            <label htmlFor="displayOrder">Display Order</label>
            <input
              type="number"
              id="displayOrder"
              name="displayOrder"
              value={formData.displayOrder}
              onChange={handleChange}
              min={0}
            />
          </div>
          <div className="modal-actions">
            <button type="button" className="secondary" onClick={onClose}>Cancel</button>
            <button
              type="submit"
              className="btn-primary"
              disabled={mutation.isPending || !formData.name.trim()}
            >
              {mutation.isPending ? 'Creating...' : 'Create Procedure'}
            </button>
          </div>
          {mutation.isError && (
            <p className="error">Error creating procedure. Please try again.</p>
          )}
        </form>
      </div>
    </div>
  );
}
